import { NavLink, useLocation } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/App";
import {
  LayoutDashboard, Factory, Package, Truck, FlaskConical, Wrench,
  ShieldCheck, Zap, ChevronLeft, ChevronRight, ChevronDown, ChevronUp,
  Boxes, ClipboardList, FileText, DollarSign, Users, Brain, FileInput,
  BarChart3, MessageSquare, CheckSquare,
} from "lucide-react";
import { PravaahLogo } from "@/components/PravaahLogo";

const FactorySidebar = ({ collapsed, setCollapsed }) => {
  const location = useLocation();
  const { user } = useAuth();
  
  // Get visible sections - super admin sees all, factory admin checks permissions
  const visibleSections = user?.role === "SUPER_ADMIN"
    ? null  // null = show all
    : (user?.factory_visible_sections || []);
  
  const canSee = (sectionId) => {
    if (!visibleSections) return true;  // Super Admin
    return visibleSections.includes(sectionId);
  };

  const allGroups = [
    {
      id: "production",
      label: "Production",
      icon: Factory,
      basePath: "/factory/production",
      matchPaths: ["/factory/production", "/factory/raw-materials", "/factory/finished-goods"],
      items: [
        { icon: ClipboardList, label: "Production Log", path: "/factory/production", section: "production", testId: "factory-sidebar-production" },
        { icon: Boxes, label: "Raw Materials", path: "/factory/raw-materials", section: "raw-materials", testId: "factory-sidebar-raw-materials" },
        { icon: Package, label: "Finished Goods", path: "/factory/finished-goods", section: "finished-goods", testId: "factory-sidebar-finished-goods" },
      ],
    },
    {
      id: "logistics",
      label: "Logistics",
      icon: Truck,
      basePath: "/factory/dispatch",
      matchPaths: ["/factory/dispatch"],
      items: [
        { icon: Truck, label: "Dispatch Queue", path: "/factory/dispatch", section: "dispatch", testId: "factory-sidebar-dispatch" },
      ],
    },
    {
      id: "operations",
      label: "Operations",
      icon: FileInput,
      basePath: "/factory/material-entries",
      matchPaths: ["/factory/material-entries", "/factory/daily-reports", "/factory/expenses", "/factory/manpower"],
      items: [
        { icon: FileInput, label: "Material Entry Log", path: "/factory/material-entries", section: "material-entries", testId: "factory-sidebar-material-entries" },
        { icon: FileText, label: "Daily Reports", path: "/factory/daily-reports", section: "daily-reports", testId: "factory-sidebar-daily-reports" },
        { icon: DollarSign, label: "Expenses", path: "/factory/expenses", section: "expenses", testId: "factory-sidebar-expenses" },
        { icon: Users, label: "HR / Manpower", path: "/factory/manpower", section: "manpower", testId: "factory-sidebar-manpower" },
      ],
    },
    {
      id: "quality",
      label: "Quality & Safety",
      icon: FlaskConical,
      basePath: "/factory/qc",
      matchPaths: ["/factory/qc", "/factory/safety"],
      items: [
        { icon: FlaskConical, label: "Quality Control", path: "/factory/qc", section: "qc", testId: "factory-sidebar-qc" },
        { icon: ShieldCheck, label: "Safety", path: "/factory/safety", section: "safety", testId: "factory-sidebar-safety" },
      ],
    },
    {
      id: "maintenance",
      label: "Maintenance",
      icon: Wrench,
      basePath: "/factory/equipment",
      matchPaths: ["/factory/equipment", "/factory/energy"],
      items: [
        { icon: Wrench, label: "Equipment", path: "/factory/equipment", section: "equipment", testId: "factory-sidebar-equipment" },
        { icon: Zap, label: "Energy", path: "/factory/energy", section: "energy", testId: "factory-sidebar-energy" },
      ],
    },
    {
      id: "intelligence",
      label: "Intelligence",
      icon: Brain,
      basePath: "/factory/forecast",
      matchPaths: ["/factory/forecast", "/factory/reports"],
      items: [
        { icon: Brain, label: "AI Forecast", path: "/factory/forecast", section: "forecast", testId: "factory-sidebar-forecast" },
        { icon: BarChart3, label: "Reports", path: "/factory/reports", section: "reports", testId: "factory-sidebar-reports" },
      ],
    },
    {
      id: "collaboration",
      label: "Collaboration",
      icon: MessageSquare,
      basePath: "/factory/tasks",
      matchPaths: ["/factory/tasks", "/factory/chat"],
      items: [
        { icon: CheckSquare, label: "Tasks", path: "/factory/tasks", section: "tasks", testId: "factory-sidebar-tasks" },
        { icon: MessageSquare, label: "Chat", path: "/factory/chat", section: "chat", testId: "factory-sidebar-chat" },
      ],
    },
  ];

  // Filter groups based on visibility
  const groups = allGroups.map(g => ({
    ...g,
    items: g.items.filter(item => canSee(item.section))
  })).filter(g => g.items.length > 0);

  const isGroupActive = (group) => {
    if (location.pathname.startsWith(group.basePath)) return true;
    if (group.matchPaths) {
      return group.matchPaths.some((p) => location.pathname.startsWith(p));
    }
    return false;
  };

  const initialExpanded = {};
  groups.forEach((g) => { initialExpanded[g.id] = isGroupActive(g); });
  const [expanded, setExpanded] = useState(initialExpanded);
  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const renderNavItem = (item) => {
    const isActive = location.pathname === item.path;
    return (
      <NavLink
        key={item.path}
        to={item.path}
        className={`flex items-center gap-3 px-3 py-1.5 mx-1 rounded-md text-[13px] transition-colors ${
          isActive
            ? "bg-emerald-600 text-white font-medium"
            : "text-slate-400 hover:text-white hover:bg-white/5"
        }`}
        data-testid={item.testId}
        title={collapsed ? item.label : ""}
      >
        <item.icon className="w-3.5 h-3.5 shrink-0" />
        {!collapsed && <span className="truncate">{item.label}</span>}
      </NavLink>
    );
  };

  const renderGroup = (group) => {
    const active = isGroupActive(group);
    const isOpen = expanded[group.id];
    const Icon = group.icon;

    if (collapsed) {
      return (
        <div key={group.id} className="relative group">
          <NavLink
            to={group.items[0]?.path || "#"}
            className={`flex items-center justify-center py-2 mx-2 rounded-lg ${
              active ? "bg-emerald-600/20 text-emerald-400" : "text-slate-400 hover:text-white hover:bg-white/5"
            }`}
            title={group.label}
            data-testid={`factory-sidebar-group-${group.id}`}
          >
            <Icon className="w-4 h-4" />
          </NavLink>
        </div>
      );
    }

    return (
      <div key={group.id} className="mt-0.5">
        <button
          onClick={() => toggle(group.id)}
          className={`flex items-center justify-between w-full px-3 py-2 mx-1 rounded-md text-[13px] transition-colors ${
            active ? "text-white" : "text-slate-500 hover:text-white hover:bg-white/5"
          }`}
          style={{ width: "calc(100% - 8px)" }}
          data-testid={`factory-sidebar-group-${group.id}`}
        >
          <div className="flex items-center gap-3">
            <Icon className="w-4 h-4 shrink-0" />
            <span className="font-medium tracking-wide text-xs uppercase">{group.label}</span>
          </div>
          {isOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
        {isOpen && (
          <div className="ml-3 border-l border-slate-700/40 pl-1 space-y-0.5 pb-1">
            {group.items.map(renderNavItem)}
          </div>
        )}
      </div>
    );
  };

  return (
    <aside
      className={`${collapsed ? "w-16" : "w-60"} bg-[#1C2B2B] min-h-screen flex flex-col transition-all duration-200 shrink-0`}
      data-testid="factory-sidebar"
    >
      <div className="p-4 flex items-center justify-between border-b border-emerald-900/50">
        {!collapsed ? (
          <PravaahLogo variant="full" size={32} dataTestId="factory-sidebar-logo" />
        ) : (
          <PravaahLogo variant="badge" size={32} dataTestId="factory-sidebar-logo-collapsed" />
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="text-slate-500 hover:text-white p-1 rounded"
          data-testid="factory-toggle-sidebar"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </div>

      <nav className="flex-1 py-2 overflow-y-auto space-y-0.5">
        {canSee("dashboard") && (
          <NavLink
            to="/factory"
            end
            className={`flex items-center gap-3 px-3 py-2 mx-2 rounded-lg text-sm transition-colors ${
              location.pathname === "/factory"
                ? "bg-emerald-600 text-white font-medium"
                : "text-slate-400 hover:text-white hover:bg-white/5"
            }`}
            data-testid="factory-sidebar-dashboard"
            title={collapsed ? "Dashboard" : ""}
          >
            <LayoutDashboard className="w-4 h-4 shrink-0" />
            {!collapsed && <span>Dashboard</span>}
          </NavLink>
        )}
        {groups.map(renderGroup)}
      </nav>
    </aside>
  );
};

export default FactorySidebar;
